import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/Toast';
import Landing        from './pages/Landing';
import Login          from './pages/auth/Login';
import Register       from './pages/auth/Register';
import ClientLogin    from './pages/auth/ClientLogin';
import AdminApp       from './pages/admin/AdminApp';
import MerchantApp    from './pages/merchant/MerchantApp';
import ParticulierApp from './pages/particulier/ParticulierApp';
import DriverApp      from './pages/driver/DriverApp';
import GestionnaireApp from './pages/gestionnaire/GestionnaireApp';
import Track          from './pages/Track';
import SharePosition  from './pages/SharePosition';
import ConfirmOrder   from './pages/ConfirmOrder';
import Recrutement    from './pages/Recrutement';
import { APP_ROUTES, LEGACY_ROUTE_REDIRECTS, isAdminUser } from './utils/auth';
import { hasRoleAccess, getStoredDriver, getProfileForRole, getStoredProfile, setStoredProfile, getStoredAppPath } from './utils/session';
import { supabase } from './supabase';
import InstallPrompt from './components/InstallPrompt';

function SmartLanding() {
  const storedPath = getStoredAppPath();
  const [adminPath, setAdminPath] = useState(null);
  const [checked, setChecked] = useState(!!storedPath);

  useEffect(() => {
    if (storedPath) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user && isAdminUser(session.user)) setAdminPath(APP_ROUTES.admin);
      setChecked(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (storedPath) return <Navigate to={storedPath} replace />;
  if (!checked) return null;
  if (adminPath) return <Navigate to={adminPath} replace />;
  return <Landing />;
}

function RoleRoute({ role, element }) {
  if (!hasRoleAccess(role)) return <Navigate to={APP_ROUTES.home} replace />;
  const roleProfile = getProfileForRole(role);
  if (!roleProfile) return <Navigate to={APP_ROUTES.home} replace />;
  const active = getStoredProfile();
  if (!active || active.id !== roleProfile.id) setStoredProfile(roleProfile);
  return element;
}

function DriverRoute() {
  const hasDriverSession = !!getStoredDriver() || hasRoleAccess('driver');
  return hasDriverSession ? <DriverApp /> : <Navigate to={APP_ROUTES.home} replace />;
}

export default function App() {
  return (
    <ToastProvider>
      <InstallPrompt />
      <BrowserRouter>
        <Routes>
          {/* Landing page — redirige vers l'interface si session active */}
          <Route path={APP_ROUTES.landing}         element={<SmartLanding />} />

          {/* Authentification */}
          <Route path={APP_ROUTES.home}            element={<Login />} />
          <Route path={APP_ROUTES.register}        element={<Register />} />
          <Route path={APP_ROUTES.clientLogin}     element={<ClientLogin />} />

          {/* Redirects legacy */}
          {Object.entries(LEGACY_ROUTE_REDIRECTS).map(([path, target]) => (
            <Route key={path} path={path} element={<Navigate to={target} replace />} />
          ))}

          {/* Apps protégées */}
          <Route path={APP_ROUTES.admin}          element={<AdminApp />} />
          <Route path={APP_ROUTES.gestionnaire}   element={<RoleRoute role="gestionnaire" element={<GestionnaireApp />} />} />
          <Route path={APP_ROUTES.merchant}       element={<RoleRoute role="merchant" element={<MerchantApp />} />} />
          <Route path={APP_ROUTES.particulier}    element={<RoleRoute role="particulier" element={<ParticulierApp />} />} />
          <Route path={APP_ROUTES.driver}         element={<DriverRoute />} />

          {/* Suivi public */}
          <Route path={APP_ROUTES.track}         element={<Track />} />
          <Route path={`${APP_ROUTES.track}/:id`} element={<Track />} />

          {/* Partage de position client — page publique mono-tâche, sans compte */}
          <Route path={`${APP_ROUTES.sharePosition}/:id`} element={<SharePosition />} />
          {/* Lien envoyé par SMS au client : confirmation de sa commande, sans connexion */}
          <Route path={`${APP_ROUTES.confirmOrder}/:token`} element={<ConfirmOrder />} />

          {/* Recrutement public */}
          <Route path="/recrutement" element={<Recrutement />} />

          <Route path="*"                         element={<Navigate to={APP_ROUTES.landing} replace />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
